import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { CalendarClock, Check, Copy, Loader2, Mail, Trash2 } from "lucide-react";
import {
  blockBookingSlot,
  emailBookingLead,
  getBookingBoard,
  removeBookingBlock,
  saveBookingSettings,
  type BookingSettings,
} from "@/lib/bookings.functions";


export const Route = createFileRoute("/_authenticated/admin/bokningar")({
  head: () => ({
    meta: [
      { title: "Bokningar — Fenny admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: BookingsPage,
});

const WEEKDAYS = [
  { n: 1, label: "Mån" },
  { n: 2, label: "Tis" },
  { n: 3, label: "Ons" },
  { n: 4, label: "Tors" },
  { n: 5, label: "Fre" },
  { n: 6, label: "Lör" },
  { n: 7, label: "Sön" },
];

const dt = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Europe/Stockholm",
  dateStyle: "short",
  timeStyle: "short",
});

function BookingsPage() {
  const qc = useQueryClient();
  const boardFn = useServerFn(getBookingBoard);
  const saveFn = useServerFn(saveBookingSettings);
  const blockFn = useServerFn(blockBookingSlot);
  const unblockFn = useServerFn(removeBookingBlock);

  const board = useQuery({
    queryKey: ["admin", "bookings"],
    queryFn: () => boardFn({ data: { days: 30 } }),
  });

  const [draft, setDraft] = useState<BookingSettings | null>(null);
  useEffect(() => {
    if (board.data && !draft) setDraft(board.data.settings);
  }, [board.data, draft]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "bookings"] });
  const save = useMutation({
    mutationFn: (s: BookingSettings) => saveFn({ data: s }),
    onSuccess: invalidate,
  });
  const block = useMutation({
    mutationFn: (v: { starts_at: string; ends_at: string; note?: string; lead_id?: string; lead_kind?: string }) =>
      blockFn({ data: v }),
    onSuccess: invalidate,
  });
  const unblock = useMutation({
    mutationFn: (id: string) => unblockFn({ data: { id } }),
    onSuccess: invalidate,
  });
  const mailFn = useServerFn(emailBookingLead);
  const [mailState, setMailState] = useState<Record<string, "sending" | "sent" | "failed">>({});
  const emailLead = async (key: string, v: { lead_id: string; kind: "kontakt" | "offert" | "ringupp"; slot_starts_at?: string }) => {
    setMailState((s) => ({ ...s, [key]: "sending" }));
    try {
      const res = await mailFn({ data: v });
      setMailState((s) => ({ ...s, [key]: res.ok ? "sent" : "failed" }));
    } catch {
      setMailState((s) => ({ ...s, [key]: "failed" }));
    }
  };



  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (id: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied((c) => (c === id ? null : c)), 2000);
  };

  const data = board.data;

  return (
    <div className="p-4 md:p-8 space-y-6">
      <header className="space-y-2">
        <h1 className="text-xl font-semibold tracking-tight">Bokningar</h1>
        <p className="text-sm text-white/60 max-w-2xl">
          Varje förfrågan får förslag på konkreta tider utifrån er tillgänglighet. Är kalendern full
          byts förslaget mot ett mejlsvar — hey@fenny.se är alltid öppen.
        </p>
      </header>

      {board.isLoading && (
        <div className="flex items-center gap-2 text-white/60 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Hämtar förfrågningar…
        </div>
      )}
      {board.isError && <p className="text-sm text-red-400">Kunde inte hämta bokningsvyn.</p>}

      {data && draft && (
        <>
          <div
            className={`rounded-xl border p-4 text-sm ${
              data.fully_booked
                ? "border-amber-400/30 bg-amber-400/10 text-amber-200"
                : "border-white/10 bg-white/[0.02] text-white/70"
            }`}
          >
            {data.fully_booked ? (
              <>
                <strong className="font-semibold">Fullbokat.</strong> Inga tider föreslås just nu —
                varje förfrågan får i stället ett mejlsvar med löfte om att återkomma.
              </>
            ) : (
              <>
                <strong className="font-semibold">{data.available_slots} lediga tider</strong> inom{" "}
                {draft.horizon_days} dagar.
              </>
            )}
          </div>

          {/* Tillgänglighet */}
          <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-4">
            <h2 className="text-sm font-semibold">Tillgänglighet</h2>

            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((d) => {
                const on = draft.weekdays.includes(d.n);
                return (
                  <button
                    key={d.n}
                    onClick={() =>
                      setDraft({
                        ...draft,
                        weekdays: on ? draft.weekdays.filter((w) => w !== d.n) : [...draft.weekdays, d.n].sort(),
                      })
                    }
                    className={`rounded-full px-3 py-1.5 text-sm ${
                      on ? "bg-white text-black" : "bg-white/5 text-white/70 hover:bg-white/10"
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>

            <div className="space-y-2">
              {draft.windows.map((w, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="time"
                    value={w.start}
                    onChange={(e) => {
                      const windows = [...draft.windows];
                      windows[i] = { ...w, start: e.target.value };
                      setDraft({ ...draft, windows });
                    }}
                    className="rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-sm"
                  />
                  <span className="text-white/40">–</span>
                  <input
                    type="time"
                    value={w.end}
                    onChange={(e) => {
                      const windows = [...draft.windows];
                      windows[i] = { ...w, end: e.target.value };
                      setDraft({ ...draft, windows });
                    }}
                    className="rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-sm"
                  />
                  <button
                    onClick={() => setDraft({ ...draft, windows: draft.windows.filter((_, j) => j !== i) })}
                    className="text-white/40 hover:text-red-400"
                    aria-label="Ta bort tidsintervall"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setDraft({ ...draft, windows: [...draft.windows, { start: "09:00", end: "12:00" }] })}
                className="rounded-full bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10"
              >
                + Lägg till tidsintervall
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Num
                label="Möteslängd (min)"
                value={draft.slot_minutes}
                onChange={(v) => setDraft({ ...draft, slot_minutes: v })}
              />
              <Num
                label="Framförhållning (tim)"
                value={draft.lead_time_hours}
                onChange={(v) => setDraft({ ...draft, lead_time_hours: v })}
              />
              <Num
                label="Visa dagar framåt"
                value={draft.horizon_days}
                onChange={(v) => setDraft({ ...draft, horizon_days: v })}
              />
              <Num
                label="Förslag per förfrågan"
                value={draft.suggestions_per_lead}
                onChange={(v) => setDraft({ ...draft, suggestions_per_lead: v })}
              />
            </div>

            <label className="block space-y-1">
              <span className="text-xs text-white/50">Undantagsdagar (semester, inspelning) — ett datum per rad</span>
              <textarea
                rows={2}
                value={draft.exception_dates.join("\n")}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    exception_dates: e.target.value
                      .split(/\s|,|;/)
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm"
                placeholder="2026-09-24"
              />
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.fully_booked}
                onChange={(e) => setDraft({ ...draft, fully_booked: e.target.checked })}
              />
              Markera som fullbokat (inga tider föreslås)
            </label>

            <label className="block space-y-1">
              <span className="text-xs text-white/50">Svarstext vid fullbokat</span>
              <textarea
                rows={3}
                value={draft.fallback_message}
                onChange={(e) => setDraft({ ...draft, fallback_message: e.target.value })}
                className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm"
              />
            </label>

            <button
              onClick={() => save.mutate(draft)}
              disabled={save.isPending}
              className="rounded-full bg-white px-4 py-2 text-sm font-medium text-black disabled:opacity-60"
            >
              {save.isPending ? "Sparar…" : "Spara tillgänglighet"}
            </button>
          </section>

          {/* Blockerade tider */}
          {data.blocks.length > 0 && (
            <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-2">
              <h2 className="text-sm font-semibold">Upptagna tider</h2>
              <ul className="space-y-1 text-sm">
                {data.blocks.map((b) => (
                  <li key={b.id} className="flex items-center justify-between gap-3">
                    <span className="text-white/80">
                      {dt.format(new Date(b.starts_at))} {b.note ? `· ${b.note}` : ""}
                    </span>
                    <button
                      onClick={() => unblock.mutate(b.id)}
                      className="text-xs text-white/50 hover:text-red-400"
                    >
                      Frigör
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Förfrågningar */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold">Förfrågningar (30 dagar)</h2>
            {data.leads.length === 0 && (
              <p className="text-sm text-white/50">Inga förfrågningar i perioden ännu.</p>
            )}
            <div className="grid gap-3 lg:grid-cols-2">
              {data.leads.map((lead) => (
                <article key={`${lead.kind}-${lead.id}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">{lead.name}</div>
                      <div className="text-xs text-white/50">
                        {lead.kind_label} · {dt.format(new Date(lead.created_at))}
                        {lead.company ? ` · ${lead.company}` : ""}
                      </div>
                      <div className="text-xs text-white/50">
                        {lead.email ?? lead.phone ?? "—"}
                        {lead.source_path ? ` · ${lead.source_path}` : ""}
                      </div>
                    </div>
                    {lead.booked && (
                      <span className="rounded-full bg-emerald-400/15 px-2 py-1 text-xs text-emerald-300">
                        Tid bokad
                      </span>
                    )}
                  </div>

                  {lead.message && (
                    <p className="text-sm text-white/70 line-clamp-3 whitespace-pre-line">{lead.message}</p>
                  )}

                  {lead.slots.length > 0 ? (
                    <div className="space-y-1.5">
                      <div className="text-xs text-white/50">Föreslå tid</div>
                      {lead.slots.map((s) => (
                        <div key={s.starts_at} className="flex items-center justify-between gap-2 text-sm">
                          <span className="text-white/80">{s.label}</span>
                          <button
                            onClick={() => {
                              block.mutate({
                                starts_at: s.starts_at,
                                ends_at: s.ends_at,
                                note: `Bokad med ${lead.name}`,
                                lead_id: lead.id,
                                lead_kind: lead.kind,
                              });
                              void emailLead(`${lead.id}-${s.starts_at}`, {
                                lead_id: lead.id,
                                kind: lead.kind,
                                slot_starts_at: s.starts_at,
                              });
                            }}
                            className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2.5 py-1 text-xs hover:bg-white/10"
                          >
                            <CalendarClock className="h-3 w-3" />
                            {mailState[`${lead.id}-${s.starts_at}`] === "sent"
                              ? "Bokad · mejlad"
                              : mailState[`${lead.id}-${s.starts_at}`] === "sending"
                                ? "Bokar…"
                                : "Boka + mejla oss"}
                          </button>

                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="rounded-lg bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
                      Fullbokat — svara i stället med mejlet nedan.
                    </p>
                  )}

                  <details className="rounded-lg bg-black/30 p-3">
                    <summary className="cursor-pointer text-xs text-white/60">Svarsförslag</summary>
                    <div className="mt-2 space-y-2">
                      <div className="text-xs text-white/50">Ämne: {lead.reply_subject}</div>
                      <pre className="whitespace-pre-wrap text-xs text-white/80">{lead.reply_text}</pre>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => copy(lead.id, lead.reply_text)}
                          className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10"
                        >
                          {copied === lead.id ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                          {copied === lead.id ? "Kopierat" : "Kopiera svar"}
                        </button>
                        {lead.email && (
                          <a
                            href={`mailto:${lead.email}?subject=${encodeURIComponent(lead.reply_subject)}&body=${encodeURIComponent(lead.reply_text)}`}
                            className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10"
                          >
                            <Mail className="h-3 w-3" /> Öppna mejl
                          </a>
                        )}
                        <button
                          onClick={() => void emailLead(lead.id, { lead_id: lead.id, kind: lead.kind })}
                          disabled={mailState[lead.id] === "sending"}
                          className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10 disabled:opacity-60"
                        >
                          <Mail className="h-3 w-3" />
                          {mailState[lead.id] === "sending"
                            ? "Skickar…"
                            : mailState[lead.id] === "sent"
                              ? "Skickat till hey@fenny.se"
                              : mailState[lead.id] === "failed"
                                ? "Kunde inte skicka"
                                : "Mejla oss leadet"}
                        </button>
                      </div>
                      {mailState[lead.id] === "failed" && (
                        <p className="text-xs text-amber-300">
                          Utskicket gick inte igenom — avsändardomänen är inte klar än. Leadet finns kvar här.
                        </p>
                      )}

                    </div>
                  </details>
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Num({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-white/50">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm tabular-nums"
      />
    </label>
  );
}
